/* eslint-disable react/jsx-props-no-spreading */
import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { Icons, Positions, Sizes } from '@sede-enterprise/shell-ds-react-framework';
import { FormProvider, useForm } from 'react-hook-form';
import { Modal } from '../../NominationGroups/style';
import { Col, FormRow } from 'src/UI/Grid';
import Button from 'src/UI/Button';
import ErrorMessage from 'src/UI/ErrorMessage';
import FormControlTextInput from 'src/UI/FormComponent/FormControlTextInput';
import FormControlSelect from 'src/UI/FormComponent/FormControlSelect';
import FormControlDatePicker from 'src/UI/FormComponent/FormControlDatePicker';
import { get, post } from 'src/services/GenesisApi';
import { sendNotification } from 'src/UI/Notification';
import {
  GET_ORGANIZATIONS,
  MAX_LENGTH_50,
  MESSAGES,
  ParameterScope,
  SET_LOCATION,
} from 'src/constants/EUMarkets';
import {
  IAddEditLocation,
  ILocationParameterDto,
  IOrganization,
  INGParameters,
  IParameterDefinition,
} from 'src/types/EUMarkets.type';
import { ISelectOptions } from 'src/types/Common.type';
import { handleError } from 'src/utils/common/utils';
import {
  formatDateOnly,
  areParameterDatesWithinParentRange,
  hasOverlappingParameterDates,
  isDateRangeValid,
  withResolvedDefinitionIds,
} from '../AddEditParameter';
import InlineParameterList, { getParameterKey } from '../AddEditParameter/InlineParameterList';

const { Add: AddIcon } = Icons;

const defaultValues = {
  locationName: '',
  locationType: '',
  organizationId: null,
  validFrom: dayjs(),
  validTo: null,
};

const locationTypeOptions = [
  { id: 'VTP', name: 'VTP' },
  { id: 'IP', name: 'IP' },
  { id: 'Storage', name: 'Storage' },
];

const TSO_ROLE_NAME = 'TSO';

const toLocationParameterDto = (parameter: INGParameters): ILocationParameterDto => ({
  parameterDefinitionId: parameter.parameterDefinitionId,
  valueString: parameter.valueString,
  valueLong: parameter.valueLong,
  valueDouble: parameter.valueDouble,
  valueBoolean: parameter.valueBoolean,
  valueListId: parameter.valueListId ?? null,
  valueListValue: parameter.valueListValue ?? null,
  validFrom: parameter.validFrom,
  validTo: parameter.validTo,
});

const parameterDraftFields: Array<keyof INGParameters> = [
  'parameterDefinitionId',
  'valueString',
  'valueLong',
  'valueDouble',
  'valueBoolean',
  'validFrom',
  'validTo',
];

const isSameParameterDraft = (previous: INGParameters | undefined, next: INGParameters) =>
  previous !== undefined && parameterDraftFields.every((field) => previous[field] === next[field]);

const removeAndReindexParameterRecord = <T,>(records: Record<number, T>, parameterIndex: number) =>
  Object.entries(records).reduce<Record<number, T>>((next, [key, value]) => {
    const index = Number(key);
    if (index === parameterIndex) {
      return next;
    }

    const updatedIndex = index > parameterIndex ? index - 1 : index;
    next[updatedIndex] = value;
    return next;
  }, {});

const AddEditLocation: FC<IAddEditLocation> = ({
  open = false,
  onClose = () => {},
  location = null,
  refreshData = () => {},
}) => {
  const methods = useForm();
  const { reset, watch, setValue, setError, clearErrors } = methods;
  const [loading, setLoading] = useState<boolean>(false);
  const [organizations, setOrganizations] = useState<ISelectOptions[]>([]);
  const [loadingOrganizations, setLoadingOrganizations] = useState<boolean>(false);
  const [parameters, setParameters] = useState<INGParameters[]>([]);
  const [parameterDrafts, setParameterDrafts] = useState<Record<number, INGParameters>>({});
  const [parameterDraftErrors, setParameterDraftErrors] = useState<Record<number, string>>({});
  const [pendingParameterCount, setPendingParameterCount] = useState(0);
  const [pendingParameterIds, setPendingParameterIds] = useState<string[]>([]);
  const pendingParameterIdRef = useRef(0);
  const validFrom = watch?.('validFrom');
  const validTo = watch?.('validTo');
  const parametersErrorMessage = methods.formState?.errors?.parameters?.message as
    string | undefined;

  const hasTsoRole = (organization: IOrganization) =>
    organization.roles?.some((role) => role.roleName === TSO_ROLE_NAME);

  const toOrganizationOption = (organization: IOrganization) => ({
    id: organization.id,
    name: organization.organizationName,
  });

  useEffect(() => {
    if (!open) {
      return;
    }

    setLoadingOrganizations(true);

    get(GET_ORGANIZATIONS)
      .then((response) => {
        const fetchedOrganizations = (response.data || [])
          .filter(hasTsoRole)
          .map(toOrganizationOption);

        setOrganizations(fetchedOrganizations);
      })
      .catch((error) => {
        handleError(error, MESSAGES.SERVER_ERROR);
      })
      .finally(() => {
        setLoadingOrganizations(false);
      });
  }, [open]);

  useEffect(() => {
    if (location) {
      reset({
        locationName: location.locationName,
        locationType: location.locationType,
        organizationId: location.organizationId,
        validFrom: location.validFrom ? dayjs(location.validFrom) : undefined,
        validTo: location.validTo ? dayjs(location.validTo) : null,
      });
      setParameters(location.parameters ?? []);
      setParameterDrafts({});
      setParameterDraftErrors({});
      setPendingParameterCount(0);
      setPendingParameterIds([]);
    } else {
      reset(defaultValues);
      setParameters([]);
      setParameterDrafts({});
      setParameterDraftErrors({});
      setPendingParameterCount(0);
      setPendingParameterIds([]);
    }
  }, [location, open]);

  const handleClose = () => {
    reset(defaultValues);
    setParameters([]);
    setParameterDrafts({});
    setParameterDraftErrors({});
    setPendingParameterCount(0);
    setPendingParameterIds([]);
    onClose();
  };

  const handleValidFromChange = (newValidFrom: Dayjs | null) => {
    if (newValidFrom && validTo && dayjs(newValidFrom).isAfter(dayjs(validTo), 'day')) {
      setValue('validTo', null, { shouldValidate: true });
    }
  };

  const handleAddParameter = () => {
    setPendingParameterCount((count) => count + 1);

    const newId = `location-parameter-${pendingParameterIdRef.current}`;
    pendingParameterIdRef.current += 1;

    setPendingParameterIds((ids) => [...ids, newId]);
  };

  const handleDefinitionsLoaded = useCallback((definitions: IParameterDefinition[]) => {
    setParameters((currentParameters) => withResolvedDefinitionIds(currentParameters, definitions));
  }, []);

  // Refreshes the banner only once one is showing, so newly added rows don't error before a save.
  const syncParametersError = useCallback(
    (nextErrors: Record<number, string>) => {
      const firstError = Object.values(nextErrors).find(Boolean);
      if (!firstError) {
        clearErrors('parameters');
        return;
      }
      if (parametersErrorMessage) {
        setError('parameters', { type: 'validate', message: firstError });
      }
    },
    [clearErrors, setError, parametersErrorMessage],
  );

  const handleParameterDraftChange = useCallback(
    (parameter: INGParameters, rowIndex: number, error?: string) => {
      setParameterDrafts((currentDrafts) => {
        const previous = currentDrafts[rowIndex];
        if (isSameParameterDraft(previous, parameter)) {
          return currentDrafts;
        }
        return { ...currentDrafts, [rowIndex]: parameter };
      });
      setParameterDraftErrors((currentErrors) => {
        const nextErrors = { ...currentErrors, [rowIndex]: error ?? '' };
        syncParametersError(nextErrors);
        return nextErrors;
      });
    },
    [syncParametersError],
  );

  const handleParameterRemove = useCallback(
    (parameterIndex: number) => {
      setParameters((currentParameters) =>
        currentParameters.filter((_, index) => index !== parameterIndex),
      );
      setParameterDrafts((currentDrafts) =>
        removeAndReindexParameterRecord(currentDrafts, parameterIndex),
      );
      setParameterDraftErrors((currentErrors) => {
        const nextErrors = removeAndReindexParameterRecord(currentErrors, parameterIndex);
        if (!Object.values(nextErrors).some(Boolean)) {
          clearErrors('parameters');
        }
        return nextErrors;
      });
    },
    [clearErrors],
  );

  const handleCancelParameterAdd = useCallback(
    (parameterIndex?: number) => {
      if (parameterIndex === undefined) {
        return;
      }
      setPendingParameterCount((currentCount) => Math.max(0, currentCount - 1));
      setPendingParameterIds((ids) =>
        ids.filter((_, index) => index !== parameterIndex - parameters.length),
      );
      setParameterDrafts((currentDrafts) =>
        removeAndReindexParameterRecord(currentDrafts, parameterIndex),
      );
      setParameterDraftErrors((currentErrors) => {
        const nextErrors = removeAndReindexParameterRecord(currentErrors, parameterIndex);
        if (!Object.values(nextErrors).some(Boolean)) {
          clearErrors('parameters');
        }
        return nextErrors;
      });
    },
    [clearErrors],
  );

  const handleOnSubmit = (formData) => {
    const pendingParameterIndexes = Array.from(
      { length: pendingParameterCount },
      (_, index) => parameters.length + index,
    );
    const mergedParametersRaw = [
      ...parameters.map((parameter, index) => parameterDrafts[index] ?? parameter),
      ...pendingParameterIndexes
        .map((index) => parameterDrafts[index])
        .filter((parameter): parameter is INGParameters => Boolean(parameter)),
    ];
    const mergedParameters = mergedParametersRaw.filter(
      (parameter, index, allParameters) =>
        allParameters.findIndex(
          (candidate) => getParameterKey(candidate) === getParameterKey(parameter),
        ) === index,
    );
    const parameterValidationError = Object.values(parameterDraftErrors).find(Boolean);
    if (parameterValidationError) {
      setError('parameters', {
        type: 'validate',
        message: parameterValidationError,
      });
      return;
    }
    if (pendingParameterIndexes.some((index) => !parameterDrafts[index])) {
      setError('parameters', {
        type: 'validate',
        message: MESSAGES.REQUIRED_PARAMETER_DEFINITION,
      });
      return;
    }
    const locationValidFrom = formData.validFrom as Dayjs | undefined;
    const locationValidTo = (formData.validTo as Dayjs | null) ?? null;
    if (
      mergedParametersRaw.some(
        (parameter) =>
          !areParameterDatesWithinParentRange(parameter, locationValidFrom, locationValidTo),
      )
    ) {
      setError('parameters', {
        type: 'validate',
        message: MESSAGES.PARAMETER_DATE_OUTSIDE_LOCATION_RANGE,
      });
      return;
    }
    if (hasOverlappingParameterDates(mergedParametersRaw)) {
      setError('parameters', {
        type: 'validate',
        message: MESSAGES.OVERLAPPING_PARAMETER_DATE_RANGE,
      });
      return;
    }
    clearErrors('parameters');
    setLoading(true);
    const failureMessage = location
      ? MESSAGES.UPDATED_LOCATION_FAILED
      : MESSAGES.CREATED_LOCATION_FAILED;
    post(SET_LOCATION, {
      id: location?.id ?? 0,
      locationName: formData.locationName,
      locationType: formData.locationType,
      organizationId: Number(formData.organizationId),
      isActive: location?.isActive ?? true,
      validFrom: formatDateOnly(formData.validFrom as Dayjs),
      validTo: formData.validTo ? formatDateOnly(formData.validTo as Dayjs) : null,
      parameters: mergedParameters.map(toLocationParameterDto),
    })
      .then(() => {
        sendNotification({
          type: 'success',
          message: location ? MESSAGES.UPDATED_LOCATION : MESSAGES.CREATED_LOCATION,
        });
        refreshData();
        handleClose();
      })
      .catch((error) => {
        handleError(error, failureMessage);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const renderFooter = () => (
    <div className='button-style'>
      <Button type='button' onClick={handleClose} variant='outlined' data-testid='btn-cancel'>
        Cancel
      </Button>
      <Button
        data-testid='submit-form'
        type='button'
        loading={loading}
        disabled={loading}
        onClick={methods.handleSubmit(handleOnSubmit)}
      >
        Save
      </Button>
    </div>
  );

  return (
    <Modal
      className='parameter-edit-modal'
      title={`${location ? 'Modify' : 'Add a new'} location`}
      open={open}
      onClose={handleClose}
      footer={renderFooter()}
      maskClosable={false}
      isDraggable
      width={1000}
    >
      <FormProvider {...methods}>
        <FormRow>
          <Col size={6}>
            <FormControlTextInput
              controllerProps={{
                name: 'locationName',
                rules: {
                  required: MESSAGES.REQUIRED_LOCATION_NAME,
                  maxLength: {
                    value: MAX_LENGTH_50,
                    message: MESSAGES.MAX_LENGTH_50,
                  },
                },
              }}
              label='Location Name'
              methods={methods}
              inputProps={{ placeholder: 'Enter Location Name' }}
            />
          </Col>
          <Col size={6}>
            <FormControlSelect
              controllerProps={{
                name: 'locationType',
                rules: {
                  required: MESSAGES.REQUIRED_LOCATION_TYPE,
                },
              }}
              label='Location Type'
              methods={methods}
              data={locationTypeOptions}
              isLoading={false}
            />
          </Col>
        </FormRow>
        <FormRow>
          <Col size={4}>
            <FormControlSelect
              methods={methods}
              label='Organization'
              isLoading={loadingOrganizations}
              data={organizations}
              controllerProps={{
                name: 'organizationId',
                rules: { required: MESSAGES.REQUIRED_ORGANIZATION },
              }}
            />
          </Col>
          <Col size={4}>
            <FormControlDatePicker
              methods={methods}
              label='Valid From'
              controllerProps={{
                name: 'validFrom',
                rules: {
                  required: MESSAGES.REQUIRED_VALID_FROM,
                  validate: (value) =>
                    isDateRangeValid(value, validTo) || MESSAGES.INVALID_PARAMETER_DATE_RANGE,
                },
              }}
              handleOnChange={handleValidFromChange}
            />
          </Col>
          <Col size={4}>
            <FormControlDatePicker
              methods={methods}
              label='Valid To'
              controllerProps={{
                name: 'validTo',
                rules: {
                  validate: (value) =>
                    isDateRangeValid(validFrom, value) || MESSAGES.INVALID_PARAMETER_DATE_RANGE,
                },
              }}
              inputProps={{
                disabledDate: (current: Dayjs) =>
                  validFrom ? current.isBefore(validFrom, 'day') : false,
              }}
            />
          </Col>
        </FormRow>
        <div className='group-details group-details-with-action'>
          <strong className='param-title'>Location Parameters</strong>
          <Button
            onClick={handleAddParameter}
            icon={<AddIcon />}
            size={Sizes.Small}
            variant='outlined'
            iconPosition={Positions.Right}
            data-testid='add-location-parameter'
          >
            Add Row
          </Button>
        </div>
        <div className='parameter-table'>
          <InlineParameterList
            open={open}
            scopeId={ParameterScope.Location}
            locationId={location?.id ?? 0}
            onDefinitionsLoaded={handleDefinitionsLoaded}
            parameters={parameters}
            isAdding={false}
            addingParameterCount={pendingParameterCount}
            pendingParameterIds={pendingParameterIds}
            onCancelAdd={handleCancelParameterAdd}
            onRemove={handleParameterRemove}
            onDraftChange={handleParameterDraftChange}
          />
          {parametersErrorMessage && <ErrorMessage>{parametersErrorMessage}</ErrorMessage>}
        </div>
      </FormProvider>
    </Modal>
  );
};

export default AddEditLocation;
